import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { supabase } from '../config/supabase';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'white',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.8)',
  },
  '& .MuiOutlinedInput-input': {
    color: 'white',
  },
}));

const FeedbackForm = ({ open, onClose, userEmail }) => {
  const [formData, setFormData] = useState({
    email: userEmail || '',
    feedbackType: 'suggestion',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validation
      if (!formData.email || !formData.message) {
        setError('Email and message are required');
        return;
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/feedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({
            email: userEmail || '',
            feedbackType: 'suggestion',
            subject: '',
            message: ''
          });
        }, 2000);
      } else {
        setError(data.error || 'Failed to submit feedback');
      }

    } catch (error) {
      console.error('Feedback submission error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, color: 'white' }}>
            🎉 Thank You!
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Your feedback has been submitted successfully. We'll review it and get back to you soon!
          </Typography>
        </DialogContent>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        💭 Share Your Thoughts
      </DialogTitle>
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Help us improve your experience
        </Typography>
      </Box>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
              {error}
            </Alert>
          )}

          <StyledTextField
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
            fullWidth
            disabled={loading}
          />

          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Type of Feedback
            </InputLabel>
            <Select
              value={formData.feedbackType}
              onChange={(e) => handleChange('feedbackType', e.target.value)}
              label="Type of Feedback"
              disabled={loading}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'white',
                },
              }}
            >
              <MenuItem value="suggestion">💡 Suggestion</MenuItem>
              <MenuItem value="question">❓ Question</MenuItem>
              <MenuItem value="bug_report">🐛 Bug Report</MenuItem>
              <MenuItem value="feature_request">⭐ Feature Request</MenuItem>
              <MenuItem value="other">💬 Other</MenuItem>
            </Select>
          </FormControl>

          <StyledTextField
            label="Subject (Optional)"
            value={formData.subject}
            onChange={(e) => handleChange('subject', e.target.value)}
            fullWidth
            disabled={loading}
          />

          <StyledTextField
            label="Your Message"
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            multiline
            rows={4}
            required
            fullWidth
            disabled={loading}
            placeholder="Please share your thoughts, questions, or suggestions..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': { color: 'white' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.email || !formData.message}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
            borderRadius: 25,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(45deg, #FF5252, #26A69A)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {loading ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default FeedbackForm;
